import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import './PrivacyReviewEdit.css';

const MIN_REDACTION_SIZE = 8;
// A tap (rather than a drag) moves less than this many screen pixels.
const TAP_MOVEMENT_THRESHOLD = 6;

const isTouchDevice = () =>
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

const PrivacyReviewEdit = () => {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [publishing, setPublishing] = useState(false);
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [redactionBoxes, setRedactionBoxes] = useState([]);
  const [draftRedactionBox, setDraftRedactionBox] = useState(null);
  const [activePage, setActivePage] = useState(0);
  // On touch devices, dragging an exact box over tiny text with a fingertip is
  // painful. Default to "tap to redact" — each tap drops a line-height band —
  // and let users switch to freehand drawing if they want fine control.
  const [tapToRedact, setTapToRedact] = useState(isTouchDevice());
  const imageRef = useRef(null);
  const gestureRef = useRef(null);

  useEffect(() => {
    // Include the auth token so the owner can load their own private uploads
    // (the API 404s private bills for unidentified viewers).
    const token = localStorage.getItem('token');
    fetch(`/api/vetbills/${billId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined)
      .then(response => {
        if (!response.ok) {
          throw new Error('Unable to load this upload');
        }
        return response.json();
      })
      .then(data => {
        setBill(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading upload for privacy review:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [billId]);

  const getRedactionPoint = (event) => {
    const image = imageRef.current;
    if (!image || !image.naturalWidth || !image.naturalHeight) {
      return null;
    }

    const rect = image.getBoundingClientRect();
    const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height));

    return {
      x: (x / rect.width) * image.naturalWidth,
      y: (y / rect.height) * image.naturalHeight
    };
  };

  const getDisplayBox = (box) => {
    const image = imageRef.current;
    if (!image || !image.naturalWidth || !image.naturalHeight) {
      return null;
    }

    return {
      left: `${(box.x / image.naturalWidth) * 100}%`,
      top: `${(box.y / image.naturalHeight) * 100}%`,
      width: `${(box.width / image.naturalWidth) * 100}%`,
      height: `${(box.height / image.naturalHeight) * 100}%`
    };
  };

  const normalizeBox = (start, end) => ({
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y)
  });

  // A single tap drops a redaction band sized to cover a typical line of text,
  // centered on the tap and clamped to the image. Much easier on a phone than
  // dragging an exact rectangle.
  const buildTapBand = (point) => {
    const image = imageRef.current;
    if (!image) {
      return null;
    }
    const bandWidth = image.naturalWidth * 0.5;
    const bandHeight = Math.max(image.naturalHeight * 0.045, 24);
    const x = Math.max(0, Math.min(point.x - bandWidth / 2, image.naturalWidth - bandWidth));
    const y = Math.max(0, Math.min(point.y - bandHeight / 2, image.naturalHeight - bandHeight));
    return { x, y, width: bandWidth, height: bandHeight };
  };

  const handlePointerDown = (event) => {
    const point = getRedactionPoint(event);
    if (!point) {
      return;
    }

    if (event.pointerId !== undefined) {
      try {
        event.currentTarget.setPointerCapture?.(event.pointerId);
      } catch (error) {
        // Synthetic pointer events and some browsers do not allow capture here.
      }
    }
    gestureRef.current = { startClientX: event.clientX, startClientY: event.clientY, moved: false };
    setDraftRedactionBox({ start: point, end: point });
    setSaveMessage('');
  };

  const handlePointerMove = (event) => {
    if (!draftRedactionBox) {
      return;
    }

    if (gestureRef.current) {
      const dx = Math.abs(event.clientX - gestureRef.current.startClientX);
      const dy = Math.abs(event.clientY - gestureRef.current.startClientY);
      if (dx > TAP_MOVEMENT_THRESHOLD || dy > TAP_MOVEMENT_THRESHOLD) {
        gestureRef.current.moved = true;
      }
    }

    const point = getRedactionPoint(event);
    if (point) {
      setDraftRedactionBox(current => ({ ...current, end: point }));
    }
  };

  const finishRedactionBox = () => {
    if (!draftRedactionBox) {
      return;
    }

    const gesture = gestureRef.current;
    gestureRef.current = null;
    const draggedBox = normalizeBox(draftRedactionBox.start, draftRedactionBox.end);
    const wasTap = !gesture || !gesture.moved;

    let nextBox = null;
    if (wasTap && tapToRedact) {
      nextBox = buildTapBand(draftRedactionBox.start);
    } else if (draggedBox.width >= MIN_REDACTION_SIZE && draggedBox.height >= MIN_REDACTION_SIZE) {
      nextBox = draggedBox;
    }

    if (nextBox) {
      setRedactionBoxes(current => [...current, nextBox]);
    }

    setDraftRedactionBox(null);
  };

  const removeBoxAt = (indexToRemove) => {
    setRedactionBoxes(current => current.filter((_, index) => index !== indexToRemove));
  };

  const createRedactedFile = () => new Promise((resolve, reject) => {
    const image = imageRef.current;

    if (!image || !image.naturalWidth || !image.naturalHeight) {
      reject(new Error('The upload image is not ready yet.'));
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const context = canvas.getContext('2d');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    context.fillStyle = '#111827';
    redactionBoxes.forEach(box => {
      context.fillRect(box.x, box.y, box.width, box.height);
    });

    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Could not create the redacted image.'));
        return;
      }

      resolve(new File([blob], `privacy-redacted-${Date.now()}.png`, { type: 'image/png' }));
    }, 'image/png');
  });

  const handleSave = async () => {
    if (!redactionBoxes.length) {
      setSaveMessage('Draw at least one box over the personal information you want hidden.');
      return;
    }

    setSaving(true);
    setSaveMessage('');

    try {
      const redactedFile = await createRedactedFile();
      const formData = new FormData();
      formData.append('file', redactedFile);
      formData.append('pageIndex', String(activePage));
      formData.append('redactionBoxCount', String(redactionBoxes.length));
      formData.append('privacyReview', JSON.stringify({
        redactionBoxes,
        redactionApplied: true,
        redactionBoxCount: redactionBoxes.length
      }));

      const response = await fetch(`/api/vetbills/${billId}/privacy-image`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to update privacy image');
      }

      setBill(current => ({
        ...current,
        imageUrl: data.imageUrl,
        thumbnailUrl: data.thumbnailUrl,
        pageUrls: data.pageUrls || current.pageUrls,
        redactionApplied: true,
        redactionBoxCount: data.redactionBoxCount
      }));
      setRedactionBoxes([]);
      setSaveMessage('Privacy update saved. The public image has been replaced.');
    } catch (err) {
      setSaveMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="privacy-edit-page">Loading...</div>;
  }

  if (error) {
    return <div className="privacy-edit-page">{error}</div>;
  }

  const documentType = String(bill.documentType || 'upload').toLowerCase();
  const makePublicMode = searchParams.get('makePublic') === '1' && bill.visibility === 'private';
  const hasUnsavedBoxes = redactionBoxes.length > 0;
  const billPageUrls = Array.isArray(bill.pageUrls) && bill.pageUrls.length > 0 ? bill.pageUrls : [bill.imageUrl];
  const isMultiPage = billPageUrls.length > 1;
  const activePageUrl = billPageUrls[Math.min(activePage, billPageUrls.length - 1)];

  const handleMakePublic = async () => {
    setPublishing(true);
    setSaveMessage('');

    try {
      const response = await fetch(`/api/vetbills/${billId}/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ visibility: 'public', privacyConfirmed: true })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Unable to make this upload public');
      }

      navigate('/dashboard');
    } catch (err) {
      setSaveMessage(err.message);
      setPublishing(false);
    }
  };

  return (
    <div className="privacy-edit-page">
      <button type="button" className="privacy-edit-back" onClick={() => navigate('/dashboard')}>Back to My Vet Bills</button>

      <div className="privacy-edit-header">
        <div>
          <span>Saved upload privacy</span>
          <h1>{makePublicMode ? 'Review before making public' : 'Review privacy'}</h1>
          <p>
            {makePublicMode
              ? 'This upload is about to become part of public comparison data. Check the image for personal information, draw redaction boxes over anything sensitive, then make it public below.'
              : 'Draw boxes over personal information. Saving replaces the public image but keeps the pricing data.'}
          </p>
        </div>
        <Link to={`/bills/${bill._id}`} className="privacy-edit-secondary-link">View upload details</Link>
      </div>

      <div className="privacy-edit-summary">
        <div>
          <span>Hospital</span>
          <strong>{bill.vetHospital || 'Unknown hospital'}</strong>
        </div>
        <div>
          <span>Document</span>
          <strong>{documentType}</strong>
        </div>
        <div>
          <span>Redactions saved</span>
          <strong>{bill.redactionBoxCount || 0}</strong>
        </div>
      </div>

      <section className="privacy-edit-tool">
        <div className="privacy-edit-tool-header">
          <div>
            <h2>Document image</h2>
            <p>
              {tapToRedact
                ? 'Tap over each name, address, phone number, account number, or note to cover it. Tap a box’s ✕ to remove it.'
                : 'Press and drag over names, addresses, phone numbers, account numbers, or medical notes.'}
            </p>
          </div>
          <span>{redactionBoxes.length} new box{redactionBoxes.length === 1 ? '' : 'es'}</span>
        </div>

        <div className="privacy-edit-mode" role="group" aria-label="Redaction mode">
          <button
            type="button"
            className={tapToRedact ? 'active' : ''}
            onClick={() => setTapToRedact(true)}
            aria-pressed={tapToRedact}
          >
            Tap to redact
          </button>
          <button
            type="button"
            className={!tapToRedact ? 'active' : ''}
            onClick={() => setTapToRedact(false)}
            aria-pressed={!tapToRedact}
          >
            Draw a box
          </button>
        </div>

        {isMultiPage && (
          <div className="page-pager privacy-edit-pager">
            <button
              type="button"
              onClick={() => setActivePage(p => Math.max(0, p - 1))}
              disabled={activePage === 0 || hasUnsavedBoxes || saving}
              aria-label="Previous page"
            >‹</button>
            <span>
              Page {activePage + 1} of {billPageUrls.length}
              {hasUnsavedBoxes ? ' — save or clear boxes before changing pages' : ''}
            </span>
            <button
              type="button"
              onClick={() => setActivePage(p => Math.min(billPageUrls.length - 1, p + 1))}
              disabled={activePage === billPageUrls.length - 1 || hasUnsavedBoxes || saving}
              aria-label="Next page"
            >›</button>
          </div>
        )}

        <div className="privacy-edit-canvas">
          <div className="privacy-edit-image-frame">
            <img
              ref={imageRef}
              src={activePageUrl}
              alt={`Uploaded document for privacy review${isMultiPage ? `, page ${activePage + 1}` : ''}`}
              draggable="false"
              crossOrigin="anonymous"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={finishRedactionBox}
              onPointerCancel={finishRedactionBox}
            />
            {redactionBoxes.map((box, index) => {
              const displayBox = getDisplayBox(box);
              if (!displayBox) {
                return null;
              }
              return (
                <span key={index} className="privacy-edit-redaction-box" style={displayBox}>
                  <button
                    type="button"
                    className="privacy-edit-redaction-remove"
                    onPointerDown={(e) => { e.stopPropagation(); }}
                    onMouseDown={(e) => { e.stopPropagation(); }}
                    onClick={(e) => { e.stopPropagation(); removeBoxAt(index); }}
                    aria-label={`Remove redaction box ${index + 1}`}
                  >
                    ✕
                  </button>
                </span>
              );
            })}
            {draftRedactionBox && (() => {
              const displayBox = getDisplayBox(normalizeBox(draftRedactionBox.start, draftRedactionBox.end));
              return displayBox ? <span className="privacy-edit-redaction-box draft" style={displayBox} /> : null;
            })()}
          </div>
        </div>

        <div className="privacy-edit-actions">
          <button type="button" onClick={() => setRedactionBoxes(current => current.slice(0, -1))} disabled={!redactionBoxes.length || saving}>
            Undo box
          </button>
          <button type="button" onClick={() => setRedactionBoxes([])} disabled={!redactionBoxes.length || saving}>
            Clear boxes
          </button>
          <button type="button" className="privacy-edit-save" onClick={handleSave} disabled={!redactionBoxes.length || saving}>
            {saving ? 'Saving...' : 'Save privacy update'}
          </button>
          {makePublicMode && (
            <button
              type="button"
              className="privacy-edit-publish"
              onClick={handleMakePublic}
              disabled={saving || publishing || hasUnsavedBoxes}
              title={hasUnsavedBoxes ? 'Save your redactions first' : undefined}
            >
              {publishing ? 'Publishing...' : 'Looks good — make public'}
            </button>
          )}
        </div>
        {makePublicMode && hasUnsavedBoxes && (
          <p className="privacy-edit-message">Save your redactions first, then make the upload public.</p>
        )}
        {saveMessage && <p className={saveMessage.includes('saved') ? 'privacy-edit-message success' : 'privacy-edit-message'}>{saveMessage}</p>}
      </section>
    </div>
  );
};

export default PrivacyReviewEdit;
